import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuthContext } from "../../contexts/AuthContext";
import Card from "../../components/Card";
import { 
  ArrowLeft, 
  Home, 
  BarChart3, 
  TrendingUp, 
  TrendingDown,
  Users,
  BookOpen,
  DollarSign,
  Calendar,
  Award,
  Target,
  Activity,
  PieChart,
  Download
} from "lucide-react";

interface AnalyticsData {
  userGrowth: {
    daily: number[];
    weekly: number[];
    monthly: number[];
    labels: string[];
  };
  revenueData: {
    daily: number[];
    monthly: number[];
    labels: string[];
  };
  courseStats: {
    totalCourses: number;
    publishedCourses: number;
    averagePrice: number;
    mostPopularCategory: string;
  };
  enrollmentStats: {
    total: number;
    thisMonth: number;
    lastMonth: number;
    growthRate: number;
  };
  topInstructors: Array<{
    name: string;
    email: string;
    revenue: number;
    students: number;
    courses: number;
  }>;
  topCourses: Array<{
    title: string;
    instructor: string;
    enrollments: number;
    revenue: number;
    rating: number;
  }>;
}

export default function Analytics() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "1y">("30d");

  useEffect(() => {
    if (user?.email !== "viitamint@gmail.com") {
      navigate("/");
      return;
    }
    loadAnalytics();
  }, [user, navigate, timeRange]);

  const loadAnalytics = async () => {
    try {
      // Load all data
      const [usersSnap, coursesSnap, progressSnap, paymentsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDocs(collection(db, "courses")),
        getDocs(collection(db, "progress")),
        getDocs(collection(db, "subscription_payments"))
      ]);

      const users = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const courses = coursesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const progress = progressSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const payments = paymentsSnap.docs.map(doc => doc.data());

      // Calculate user growth
      const now = new Date();
      const userGrowthData = calculateUserGrowth(users, timeRange);
      
      // Calculate revenue data
      const revenueData = calculateRevenue(payments, timeRange);
      
      // Calculate course stats
      const courseStats = {
        totalCourses: courses.length,
        publishedCourses: courses.filter((c: any) => c.published).length,
        averagePrice: courses.reduce((sum: number, c: any) => sum + (c.price || 0), 0) / courses.length || 0,
        mostPopularCategory: getMostPopularCategory(courses)
      };

      // Calculate enrollment stats
      const thisMonth = progress.filter((p: any) => {
        const createdAt = p.createdAt?.toDate?.() || new Date(0);
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return createdAt >= monthStart;
      }).length;

      const lastMonth = progress.filter((p: any) => {
        const createdAt = p.createdAt?.toDate?.() || new Date(0);
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        return createdAt >= lastMonthStart && createdAt < thisMonthStart;
      }).length;

      const enrollmentStats = {
        total: progress.length,
        thisMonth,
        lastMonth,
        growthRate: lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : 0
      };

      // Calculate top instructors (include admins)
      const instructors = users.filter((u: any) => u.role === "instructor" || u.role === "admin");
      const topInstructors = instructors.slice(0, 5).map((instructor: any) => {
        const instructorCourses = courses.filter((c: any) => c.instructorId === instructor.id);
        const courseIds = instructorCourses.map((c: any) => c.id);
        const students = new Set(
          progress.filter((p: any) => courseIds.includes(p.courseId)).map((p: any) => p.userId)
        ).size;
        const revenue = payments
          .filter((p: any) => p.instructorId === instructor.id)
          .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

        return {
          name: instructor.name || "ไม่ระบุ",
          email: instructor.email,
          revenue,
          students,
          courses: instructorCourses.length
        };
      }).sort((a, b) => b.revenue - a.revenue);

      // Calculate top courses
      const topCourses = courses.slice(0, 5).map((course: any) => {
        const enrollments = progress.filter((p: any) => p.courseId === course.id).length;
        const instructor = users.find((u: any) => u.id === course.instructorId);
        
        return {
          title: course.title,
          instructor: instructor?.name || "ไม่ระบุ",
          enrollments,
          revenue: enrollments * (course.price || 0),
          rating: course.rating || 0
        };
      }).sort((a, b) => b.enrollments - a.enrollments);

      setAnalytics({
        userGrowth: userGrowthData,
        revenueData,
        courseStats,
        enrollmentStats,
        topInstructors,
        topCourses
      });
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateUserGrowth = (users: any[], range: string) => {
    const now = new Date();
    const days = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : 365;
    const labels: string[] = [];
    const daily: number[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      
      const count = users.filter((u: any) => {
        const createdAt = u.createdAt?.toDate?.() || new Date(0);
        return createdAt >= dayStart && createdAt < dayEnd;
      }).length;

      labels.push(date.toLocaleDateString('th-TH', { month: 'short', day: 'numeric' }));
      daily.push(count);
    }

    return { daily, weekly: [], monthly: [], labels };
  };

  const calculateRevenue = (payments: any[], range: string) => {
    const now = new Date();
    const days = range === "7d" ? 7 : range === "30d" ? 30 : range === "90d" ? 90 : 365;
    const labels: string[] = [];
    const daily: number[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      
      const revenue = payments
        .filter((p: any) => {
          const paidAt = p.paidAt?.toDate?.() || new Date(0);
          return paidAt >= dayStart && paidAt < dayEnd;
        })
        .reduce((sum: number, p: any) => sum + (Number(p.amount) || 0), 0);

      labels.push(date.toLocaleDateString('th-TH', { month: 'short', day: 'numeric' }));
      daily.push(revenue);
    }

    return { daily, monthly: [], labels };
  };

  const getMostPopularCategory = (courses: any[]) => {
    const categories: { [key: string]: number } = {};
    courses.forEach((c: any) => {
      const cat = c.category || "ไม่ระบุ";
      categories[cat] = (categories[cat] || 0) + 1;
    });
    
    let maxCat = "ไม่ระบุ";
    let maxCount = 0;
    Object.entries(categories).forEach(([cat, count]) => {
      if (count > maxCount) {
        maxCount = count;
        maxCat = cat;
      }
    });
    
    return maxCat;
  };

  const exportData = () => {
    if (!analytics) return;
    
    const data = JSON.stringify(analytics, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analytics-${new Date().toISOString()}.json`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-soft">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto mb-4"></div>
          <p className="text-textLight text-lg">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  return (
    <div className="min-h-screen bg-gradient-soft py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Link to="/admin">
                <button className="p-2 hover:bg-white/50 rounded-lg transition-colors">
                  <ArrowLeft className="w-6 h-6 text-textDark" />
                </button>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-textDark flex items-center gap-3">
                  <BarChart3 className="w-10 h-10 text-indigo-600" />
                  รายงานและวิเคราะห์
                </h1>
                <p className="text-textLight">ข้อมูลเชิงลึกและสถิติการใช้งาน</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={exportData}
                className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200"
              >
                <Download className="w-5 h-5" />
                <span>ส่งออกข้อมูล</span>
              </button>
              <Link to="/">
                <button className="flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 rounded-xl font-semibold transition-all border-2 border-gray-200">
                  <Home className="w-5 h-5" />
                  <span>ไปหน้าแรก</span>
                </button>
              </Link>
            </div>
          </div>

          {/* Time Range Selector */}
          <div className="flex gap-3 mb-6">
            {(["7d", "30d", "90d", "1y"] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                  timeRange === range
                    ? "bg-gradient-primary text-white shadow-lg"
                    : "bg-white text-textDark hover:bg-gray-50"
                }`}
              >
                {range === "7d" ? "7 วัน" : range === "30d" ? "30 วัน" : range === "90d" ? "90 วัน" : "1 ปี"}
              </button>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">การลงทะเบียนเดือนนี้</p>
                <p className="text-3xl font-bold text-blue-600">{analytics.enrollmentStats.thisMonth}</p>
                <p className={`text-xs font-semibold ${
                  analytics.enrollmentStats.growthRate >= 0 ? "text-green-600" : "text-red-600"
                }`}>
                  {analytics.enrollmentStats.growthRate >= 0 ? "+" : ""}
                  {analytics.enrollmentStats.growthRate.toFixed(1)}% จากเดือนที่แล้ว
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">คอร์สที่เผยแพร่</p>
                <p className="text-3xl font-bold text-green-600">{analytics.courseStats.publishedCourses}</p>
                <p className="text-xs text-textLight">
                  จากทั้งหมด {analytics.courseStats.totalCourses} คอร์ส
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <DollarSign className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">ราคาเฉลี่ยคอร์ส</p>
                <p className="text-3xl font-bold text-blue-600">
                  ฿{analytics.courseStats.averagePrice.toFixed(0)}
                </p>
                <p className="text-xs text-textLight">ต่อคอร์ส</p>
              </div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center">
                <PieChart className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-textLight">หมวดยอดนิยม</p>
                <p className="text-lg font-bold text-blue-600">{analytics.courseStats.mostPopularCategory}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Charts */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* User Growth Chart */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-blue-600" />
                การเติบโตของผู้ใช้
              </h3>
              <div className="h-64 flex items-end justify-between gap-2">
                {analytics.userGrowth.daily.map((count, index) => {
                  const maxCount = Math.max(...analytics.userGrowth.daily);
                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div className="text-xs font-semibold text-blue-600">{count}</div>
                      <div
                        className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all hover:opacity-80"
                        style={{ height: `${height}%`, minHeight: count > 0 ? "20px" : "0" }}
                      ></div>
                      {index % Math.floor(analytics.userGrowth.daily.length / 5) === 0 && (
                        <div className="text-xs text-textLight rotate-45 origin-left">
                          {analytics.userGrowth.labels[index]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Revenue Chart */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-600" />
                รายได้
              </h3>
              <div className="h-64 flex items-end justify-between gap-2">
                {analytics.revenueData.daily.map((revenue, index) => {
                  const maxRevenue = Math.max(...analytics.revenueData.daily);
                  const height = maxRevenue > 0 ? (revenue / maxRevenue) * 100 : 0;
                  
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center gap-2">
                      <div className="text-xs font-semibold text-green-600">
                        {revenue > 0 ? `฿${(revenue / 1000).toFixed(0)}k` : ""}
                      </div>
                      <div
                        className="w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg transition-all hover:opacity-80"
                        style={{ height: `${height}%`, minHeight: revenue > 0 ? "20px" : "0" }}
                      ></div>
                      {index % Math.floor(analytics.revenueData.daily.length / 5) === 0 && (
                        <div className="text-xs text-textLight rotate-45 origin-left">
                          {analytics.revenueData.labels[index]}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>

        {/* Top Instructors & Courses */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Top Instructors */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-yellow-600" />
                ผู้สอนยอดนิยม
              </h3>
              <div className="space-y-4">
                {analytics.topInstructors.map((instructor, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center text-white font-bold">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="font-bold text-textDark">{instructor.name}</p>
                        <p className="text-sm text-textLight">{instructor.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">฿{instructor.revenue.toLocaleString()}</p>
                      <p className="text-xs text-textLight">{instructor.students} นักเรียน • {instructor.courses} คอร์ส</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>

          {/* Top Courses */}
          <Card>
            <div className="p-6">
              <h3 className="text-xl font-bold text-textDark mb-4 flex items-center gap-2">
                <Target className="w-6 h-6 text-blue-600" />
                คอร์สยอดนิยม
              </h3>
              <div className="space-y-4">
                {analytics.topCourses.map((course, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-full flex items-center justify-center text-white font-bold">
                        #{index + 1}
                      </div>
                      <div>
                        <p className="font-bold text-textDark">{course.title}</p>
                        <p className="text-sm text-textLight">โดย {course.instructor}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-blue-600">{course.enrollments} คน</p>
                      <p className="text-xs text-textLight">฿{course.revenue.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
